import React, { useEffect, createContext, useMemo, useCallback, Suspense } from 'react'
import './App.css'
import { IntlProvider } from 'react-intl'
import { RouterProvider } from 'react-router-dom'
import { getLang } from '@/store/language/language'
import { useAppDispatch, useAppSelector } from '@/hooks/appHook'
import { getUser } from '@/store/user/user'
import { getFullVersion } from '@/utils/version/version'
import router from '@/Routes/Router'
import useLogged from '@/Routes/hooks/useLogged'
import { setSize as setScreenSize } from '@/store/screenSize/screenSize'
import PagesSpinner from '@/components/Modal/Spinners/PagesSpinner/PagesSpinner'
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary'
import useCheckVersion from '@/hooks/useCheckVersion'
import useModalSimple from '@/hooks/useModalSimple'
import useTranslates from '@/hooks/useTranslates'
import globalEvents from '@/services/GlobalEvents/GlobalEvents'
import { Events } from '@/services/GlobalEvents/constants'
import RebootPanel from '@/components/RebootPanel/RebootPanel'
import UserNotify from '@/components/Modal/UserNotify/UserNotify'
import { updateUserData } from '@/utils/app'
import { setTag, setUser } from '@/services/Sentry/Sentry'

export const LoggedContext = createContext({
  isLoggedIn: false
})

const App = () => {
  const dispatch = useAppDispatch()
  const lang = useAppSelector(getLang)
  const user = useAppSelector(getUser)
  const translates = useTranslates(lang)

  const {
    show: showRebootModal,
    visible: visibleRebootModal
  } = useModalSimple()

  useCheckVersion()

  useEffect(() => {
    updateUserData({
      id: user?.$jid || '',
      label: user?.nickname || user?.memberId || ''
    })
  }, [user?.$jid, user?.nickname, user?.memberId])

  useEffect(() => {
    globalEvents.addEventListener(Events.reboot, showRebootModal)
    return () => {
      globalEvents.removeEventListener(Events.reboot, showRebootModal)
    }
  }, [showRebootModal])

  useEffect(() => {
    getFullVersion().then(ver => {
      setTag('app-version', ver)
    })
  }, [])

  useEffect(() => {
    setUser({
      id: user?.memberId
    })
  }, [user?.memberId])

  const { isLoggedIn } = useLogged()

  const loggedContext = useMemo(() => {
    return {
      isLoggedIn
    }
  }, [isLoggedIn])

  const updateScreenSize = useCallback(() => {
    dispatch(setScreenSize({
      width: document.documentElement.clientWidth || window.innerWidth,
      height: document.documentElement.clientHeight || window.innerHeight
    }))
  }, [dispatch])

  useEffect(() => {
    updateScreenSize()
    window.addEventListener('resize', updateScreenSize)
    return () => {
      window.removeEventListener('resize', updateScreenSize)
    }
  }, [updateScreenSize])

  return (
    <IntlProvider locale={lang} messages={translates}>
      <LoggedContext.Provider value={loggedContext}>
        <ErrorBoundary>
          <Suspense fallback={<PagesSpinner/>}>
            {visibleRebootModal && <RebootPanel/>}
            <RouterProvider router={router}/>
          </Suspense>
        </ErrorBoundary>
        <UserNotify/>
      </LoggedContext.Provider>
    </IntlProvider>
  )
}

export default App
